// Role-based permissions configuration
// This file defines what each role can see in the sidebar menu

export const ROLE_PERMISSIONS = {
  superadmin: ['all'], // See everything

  admin: {
    mainMenus: ['dashboard', 'profile', 'members', 'booking', 'lead', 'reports', 'visitsite', 'paymentassociate', 'commission-generation', 'inventory', 'rewards', 'project', 'associate', 'my-team', 'user-management', 'activity-logs', 'realestatemanagement'],
    subMenus: {
      'profile': ['myprofile', 'mybusiness', 'changesponsor'],
      'members': ['customer'],
      'booking': ['bookplot', 'bookingdetail', 'bookingapproval', 'receiptapproval', 'bookingreport', 'bookingcommission', 'bookingreceipt', 'addbooking', 'cancelreport'],
      'lead': ['leadlist', 'leadtimeline', 'leadreport'],
      'reports': ['associate', 'booking', 'customer', 'branch', 'lead', 'emi', 'project'],
      'associate': ['associatelist', 'commissionreport', 'followupreport', 'associateledger', 'tds', 'associate-registration', 'associate-tree', 'associate-commission', 'associate-change'],
      'customer': ['customerledger'],
      'branch': ['branchwisesummary', 'branchsummarydetails'],
      'project': ['project-list', 'plot-list', 'row-house', 'villa', 'apartment-commercial', 'projectwisesummary'],
      'user-management': ['creteroles', 'rolemaster', 'userpermission'],
      'realestatemanagement': ['activitylogs']
    }
  },

  manager: {
    mainMenus: ['dashboard', 'profile', 'members', 'booking', 'lead', 'reports', 'visitsite', 'paymentassociate', 'commission-generation', 'inventory', 'rewards', 'project', 'associate', 'my-team'],
    subMenus: {
      'profile': ['myprofile', 'changesponsor'],
      'members': ['customer'],
      'booking': ['bookplot', 'bookingdetaila', 'bookingapproval', 'receiptapproval', 'bookingreport', 'bookingcommission', 'bookingreceipt', 'addbooking', 'bookingdetail'],
      'lead': ['leadlist', 'leadtimeline', 'changeassociate', 'sitevisit', 'leadreport'],
      'reports': ['associate', 'booking', 'customer', 'branch', 'lead', 'emi'],
      'associate': ['associatelist', 'commissionreport', 'followupreport', 'associate-registration', 'associate-tree', 'associate-commission', 'associate-change'],
      'customer': ['customerledger'],
      'branch': ['branchwisesummary', 'branchsummarydetails'],
      'emi': ['emireport'],
      'project': ['project-list', 'plot-list', 'row-house', 'villa', 'apartment-commercial'],
      'rewards': ['reward-list', 'history-reward', 'redeem-requests', 'reward-transaction'],
      'my-team': ['my-team']
    }
  },

  employee: {
    mainMenus: ['dashboard', 'profile', 'members', 'booking', 'lead', 'reports', 'visitsite', 'paymentassociate', 'commission-generation', 'inventory', 'rewards', 'project', 'associate', 'my-team'],
    subMenus: {
      'profile': ['myprofile'],
      'members': ['customer'],
      'booking': ['bookplot', 'bookingdetaila', 'receiptapproval', 'bookingreport', 'bookingcommission', 'bookingreceipt', 'addbooking', 'bookingdetail'],
      'lead': ['leadlist', 'leadtimeline', 'sitevisit', 'leadreport'],
      'reports': ['associate', 'booking', 'customer', 'branch', 'lead', 'emi'],
      'associate': ['associatelist', 'commissionreport', 'followupreport'],
      'customer': ['customerledger'],
      'branch': ['branchwisesummary', 'branchsummarydetails'],
      'emi': ['emireport'],
      'my-team': ['my-team']
    }
  },

  associate: {
    mainMenus: ['dashboard', 'profile', 'members', 'associate', 'project', 'inventory', 'lead', 'booking', 'visitsite', 'my-team'],
    subMenus: {
      'profile': ['myprofile'],
      'members': ['customer'],
      'associate': ['associatelist', 'commissionreport', 'followupreport'],
      'project': ['plotlistdetails', 'projectlist', 'projectpricing', 'projectenquiry'],
      'lead': ['leadlist', 'leadtimeline', 'sitevisit'],
      'booking': ['bookplot', 'bookingdetaila', 'bookingreport', 'bookingcommission', 'receiptapproval'],
      // 'reports': ['associate', 'booking']
      'visitsite': ['sitevisit'],
      'my-team': ['my-team']
    }
  }
};

// Helper function to get user permissions
export const getUserPermissions = (userRole) => {
  return ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS.employee;
};

// Helper function to check if user has 'all' permissions
export const hasAllPermissions = (userPermissions) => {
  return Array.isArray(userPermissions) && userPermissions.includes('all');
};

// Helper function to check if main menu is allowed
export const isMainMenuAllowed = (item, userPermissions) => {
  if (hasAllPermissions(userPermissions)) {
    return true;
  }

  if (!userPermissions.mainMenus) {
    return false;
  }

  const normalize = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const itemLabelNorm = normalize(item.label);
  const itemLinkNorm = normalize(item.link);

  return userPermissions.mainMenus.some(menu => {
    const menuNorm = normalize(menu);
    return itemLinkNorm.includes(menuNorm) ||
      itemLabelNorm.includes(menuNorm) ||
      (menuNorm === 'dashboard' && item.link === '/app/dashboard');
  });
};

// Helper function to check if submenu is allowed
export const isSubMenuAllowed = (child, parentMenu, userPermissions) => {
  if (hasAllPermissions(userPermissions)) {
    return true;
  }

  if (!userPermissions.subMenus || !userPermissions.subMenus[parentMenu]) {
    return false;
  }

  const normalize = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const childLinkNorm = normalize(child.link);
  const childLabelNorm = normalize(child.label);

  return userPermissions.subMenus[parentMenu].some(subMenu => {
    const subMenuNorm = normalize(subMenu);
    return childLinkNorm.includes(subMenuNorm) || childLabelNorm.includes(subMenuNorm);
  });
};
